import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  Eye,
  FileText,
  MousePointerClick,
  Globe,
  Monitor,
  Smartphone,
  Tablet,
  RefreshCw,
} from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Spinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
} from '@mawkaa/ui';
import { useAnalyticsStore } from '@/stores/analyticsStore';
import { useSitesStore } from '@/stores/sitesStore';
import { cn } from '@mawkaa/ui';

export function AnalyticsPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { data, realtime, isLoading, period, fetchAnalytics, fetchRealtime, setPeriod } =
    useAnalyticsStore();
  const { sites, fetchSites } = useSitesStore();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const site = sites.find((s) => s.id === siteId);

  useEffect(() => {
    if (!sites.length) {
      fetchSites();
    }
  }, [sites.length, fetchSites]);

  useEffect(() => {
    if (siteId) {
      fetchAnalytics(siteId);
      fetchRealtime(siteId);

      // Refresh realtime every 30 seconds
      const interval = setInterval(() => {
        fetchRealtime(siteId);
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [siteId, period, fetchAnalytics, fetchRealtime]);

  const handleRefresh = async () => {
    if (!siteId) return;
    setIsRefreshing(true);
    await Promise.all([fetchAnalytics(siteId), fetchRealtime(siteId)]);
    setIsRefreshing(false);
  };

  const handlePeriodChange = (value: string) => {
    setPeriod(value as '7d' | '30d' | '90d');
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  const countryNames: Record<string, string> = {
    TN: 'Tunisie',
    FR: 'France',
    US: 'États-Unis',
    DE: 'Allemagne',
    GB: 'Royaume-Uni',
    CA: 'Canada',
    MA: 'Maroc',
    DZ: 'Algérie',
    LY: 'Libye',
    EG: 'Égypte',
  };

  return (
    <div className="container mx-auto py-6 px-4 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/sites')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Analytics</h1>
            {site && <p className="text-muted-foreground">{site.name}</p>}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Select value={period} onValueChange={handlePeriodChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">7 derniers jours</SelectItem>
              <SelectItem value="30d">30 derniers jours</SelectItem>
              <SelectItem value="90d">90 derniers jours</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Realtime visitors */}
      <Card className="mb-6 bg-green-50 border-green-200">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse" />
            <span className="text-lg font-semibold text-green-800">
              {realtime?.activeVisitors || 0} visiteur{(realtime?.activeVisitors || 0) > 1 ? 's' : ''} en ligne
            </span>
            <span className="text-sm text-green-600">en ce moment</span>
          </div>
        </CardContent>
      </Card>

      {/* Overview cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pages vues</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.overview.totalPageviews || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Visiteurs uniques</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.overview.uniqueVisitors || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Formulaires soumis</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.overview.formSubmissions || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pages / Session</CardTitle>
            <MousePointerClick className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.overview.avgPagesPerSession || '0'}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Pageviews chart */}
        <Card>
          <CardHeader>
            <CardTitle>Pages vues par jour</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.pageviewsByDay && data.pageviewsByDay.length > 0 ? (
              <div className="h-64 flex items-end gap-1">
                {data.pageviewsByDay.map((day, index) => {
                  const maxCount = Math.max(...data.pageviewsByDay.map((d) => d.count));
                  const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                  return (
                    <div
                      key={index}
                      className="flex-1 flex flex-col items-center gap-1"
                      title={`${day.date}: ${day.count} vues`}
                    >
                      <div
                        className="w-full bg-primary rounded-t transition-all hover:bg-primary/80"
                        style={{ height: `${Math.max(height, 4)}%` }}
                      />
                      <span className="text-xs text-muted-foreground rotate-45 origin-left">
                        {new Date(day.date).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                        })}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                Aucune donnée pour cette période
              </div>
            )}
          </CardContent>
        </Card>

        {/* Device breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Appareils</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.devices ? (
              <div className="space-y-4">
                {[
                  { label: 'Desktop', value: data.devices.desktop, icon: Monitor },
                  { label: 'Mobile', value: data.devices.mobile, icon: Smartphone },
                  { label: 'Tablette', value: data.devices.tablet, icon: Tablet },
                ].map((device) => {
                  const total =
                    data.devices.desktop + data.devices.mobile + data.devices.tablet;
                  const percentage = total > 0 ? (device.value / total) * 100 : 0;
                  return (
                    <div key={device.label} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <device.icon className="h-4 w-4 text-muted-foreground" />
                          <span>{device.label}</span>
                        </div>
                        <span className="font-medium">
                          {device.value} ({percentage.toFixed(1)}%)
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center text-muted-foreground">
                Aucune donnée
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top pages */}
        <Card>
          <CardHeader>
            <CardTitle>Pages populaires</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.topPages && data.topPages.length > 0 ? (
              <div className="space-y-3">
                {data.topPages.map((page, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm truncate flex-1 mr-2">
                      {page.path || '/'}
                    </span>
                    <Badge variant="secondary">{page.count}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">Aucune donnée</div>
            )}
          </CardContent>
        </Card>

        {/* Top referrers */}
        <Card>
          <CardHeader>
            <CardTitle>Sources de trafic</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.topReferrers && data.topReferrers.length > 0 ? (
              <div className="space-y-3">
                {data.topReferrers.map((ref, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm truncate flex-1 mr-2">
                      {ref.referrer ? new URL(ref.referrer).hostname : 'Direct'}
                    </span>
                    <Badge variant="secondary">{ref.count}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">Aucune donnée</div>
            )}
          </CardContent>
        </Card>

        {/* Top countries */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Pays
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.topCountries && data.topCountries.length > 0 ? (
              <div className="space-y-3">
                {data.topCountries.map((country, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm">
                      {countryNames[country.country] || country.country}
                    </span>
                    <Badge variant="secondary">{country.count}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">Aucune donnée</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
